import React, { useState } from "react";
import Chart from "react-apexcharts";

import { chartThemeColors as PALETTE } from "./index";

const CHART_BUTTONS = [
  {
    type: "vbar",
    title: "Vertical Column",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="4" /><line x1="12" y1="20" x2="12" y2="10" /><line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    type: "hbar",
    title: "Horizontal Bar",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="14" y2="12" /><line x1="4" y1="18" x2="18" y2="18" />
      </svg>
    ),
  },
  {
    type: "donut",
    title: "Donut Chart",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" />
      </svg>
    ),
  },
  {
    type: "pie",
    title: "Pie Chart",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><path d="M12 2a10 10 0 0 1 10 10H12V2z" />
      </svg>
    ),
  },
  {
    type: "line",
    title: "Spline Area",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
];

const ModernMultiChartWidget = ({
  heading = "Delivery",
  badgeType = "delivery",
  badgeColor = "orange",
  size = "col-6",
  data = null,
}) => {
  const [chartType, setChartType] = useState("donut");

  const list = Array.isArray(data) ? data : data?.[badgeType] || data?.details?.[badgeType];
  const rawData = list && list.length > 0 ? list : [];

  const labels = rawData.map((d) => d.name);
  const values = rawData.map((d) => d.value);
  const orders = rawData.map((d) => d.orders);
  const totalCount = values.reduce((a, b) => a + b, 0);

  // Unified hover tooltip for all chart representations
  const customTooltip = ({ series, seriesIndex, dataPointIndex }) => {
    const isCircle = chartType === "donut" || chartType === "pie";
    const idx = isCircle ? seriesIndex : (dataPointIndex >= 0 ? dataPointIndex : 0);
    const label = labels[idx] || "";
    const selected = isCircle ? (series?.[idx] ?? values[idx]) : values[idx];
    const total = isCircle && series?.length ? series.reduce((a, b) => a + parseFloat(b), 0) : totalCount;
    const percent = total > 0 ? ((parseFloat(selected) / total) * 100).toFixed(2) : "0.00";
    const order = orders[idx];
    const color = PALETTE[idx % PALETTE.length];

    return (
      `<div style="color:#ffffff;background-color:${color};padding:6px 10px;border-radius:6px;font-size:13px;line-height:1.4;box-shadow:0 3px 10px rgba(0,0,0,0.2);">` +
      `<div>${label}:${selected}</div>` +
      `<div>${percent}%</div>` +
      (order !== undefined ? `<div>Orders: ${order}</div>` : "") +
      `</div>`
    );
  };

  // Base options shared across all chart modes
  const baseOptions = {
    chart: { type: chartType, toolbar: { show: false } },
    orders,
    colors: PALETTE,
    tooltip: { enabled: true, custom: customTooltip },
    legend: {
      position: "bottom",
      fontSize: "13px",
      offsetY: 0,
      markers: { width: 10, height: 10, radius: 3 },
      itemMargin: { horizontal: 8, vertical: 4 },
    },
  };

  // ApexCharts config builder
  const getChartConfig = () => {
    if (chartType === "donut" || chartType === "pie") {
      const isDonut = chartType === "donut";
      return {
        type: chartType,
        series: values,
        options: {
          ...baseOptions,
          labels,
          fill: { type: "solid", opacity: 1 },
          stroke: { width: 2, colors: ["#ffffff"] },
          dataLabels: { enabled: !isDonut },
          plotOptions: {
            pie: {
              donut: {
                size: isDonut ? "70%" : "0%",
                labels: {
                  show: isDonut,
                  total: {
                    show: isDonut,
                    label: "Total",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#64748b",
                    formatter: () => totalCount.toLocaleString(),
                  },
                  value: { fontSize: "1.6rem", fontWeight: 700, color: "#0f172a", offsetY: 4 },
                },
              },
            },
          },
        },
      };
    }

    if (chartType === "vbar" || chartType === "hbar") {
      const isHorizontal = chartType === "hbar";
      return {
        type: "bar",
        series: [{ name: heading, data: values }],
        options: {
          ...baseOptions,
          fill: { type: "solid", opacity: 1 },
          plotOptions: {
            bar: { horizontal: isHorizontal, borderRadius: 6, columnWidth: "48%", barHeight: "58%", distributed: true },
          },
          dataLabels: { enabled: isHorizontal, style: { fontSize: "12px" } },
          xaxis: { categories: labels, labels: { rotate: isHorizontal ? 0 : -35, style: { fontSize: "12px", colors: "#64748b" } } },
          yaxis: { labels: { style: { fontSize: "12px", colors: "#64748b" } } },
          grid: { strokeDashArray: 3, borderColor: "#f1f5f9" },
        },
      };
    }

    // Line / Area Spline
    return {
      type: "area",
      series: [{ name: heading, data: values }],
      options: {
        ...baseOptions,
        colors: [badgeColor === "orange" ? "#f59e0b" : "#2563eb"],
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 5, strokeColors: "#ffffff", strokeWidth: 2 },
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.08 } },
        dataLabels: { enabled: false },
        xaxis: { categories: labels, labels: { style: { fontSize: "12px", colors: "#64748b" } } },
        yaxis: { labels: { style: { fontSize: "12px", colors: "#64748b" } } },
        grid: { strokeDashArray: 3, borderColor: "#f1f5f9" },
      },
    };
  };

  const chartConfig = getChartConfig();

  return (
    <div className={`col ${size} row mx-0 mt-3 px-2 position-relative`}>
      <div className="row px-0 mx-0 justify-content-between col col-12">
        <div className="modern-chart-card col col-12 w-100 flex-basis-100">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h5 className="mb-0 font-weight-bold" style={{ color: "#0f172a", fontSize: "1.15rem" }}>
                {heading}
              </h5>
              <span style={{ fontSize: "0.85rem", color: "#64748b" }}>
                {rawData.length} Platforms Recorded
              </span>
            </div>

            {/* Top-Right: Toggle Toolbar */}
            <div className="modern-chart-toggle-group">
              {CHART_BUTTONS.map((btn) => (
                <button
                  key={btn.type}
                  type="button"
                  className={`modern-chart-toggle-btn ${chartType === btn.type ? "active" : ""}`}
                  title={btn.title}
                  onClick={() => setChartType(btn.type)}
                >
                  {btn.icon}
                </button>
              ))}
            </div>
          </div>

          {/* Chart Display */}
          <div style={{ minHeight: "290px" }}>
            <Chart
              key={chartType}
              width="100%"
              height="290px"
              type={chartConfig.type}
              series={chartConfig.series}
              options={chartConfig.options}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernMultiChartWidget;
